/**
 * @typedef {Window & {
 * ThemeAnalytics?: {
 * pushViewItem?: (productData: object, variantData: object) => Promise<void>
 * },
 * yotpoWidgetsContainer?: {
 * initWidgets?: () => void
 * }
 * }} ThemeAnalyticsWindow
 */

class ColorSelector extends HTMLElement {
  constructor() {
    super();
    this.prefetchCache = new Map();
    this.prefetchInProgress = new Set();
    /** @type {Object<string, {url: string, hasWidthOption: boolean, options: Object[], variants: {id: number, available: boolean, options: string[]}[]}>|null} */
    this.allProductsVariants = null;
    this.hasInitialized = false;
    this.sectionRenderingColorSwapping = true;
    /**
     * Monotonic counter used to ignore stale background responses.
     * Each swatch click increments it; pending fetches abort their work
     * if the active swap has moved on by the time they resolve.
     */
    this.activeSwapId = 0;
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    if (!this.sectionId) return;
    
    if (this.dataset.sectionRenderingColorSwapping === 'false') {
      this.sectionRenderingColorSwapping = false;
    }

    if (this.hasInitialized) return;
    this.hasInitialized = true;

    this.loadAllProductsVariants();
    this.setupPrefetch();
    this.setupEventListeners();
    this.setupNavigationHandling();

    /** @type {ThemeAnalyticsWindow} */
    const themeAnalyticsWindow = window;
    themeAnalyticsWindow.yotpoWidgetsContainer?.initWidgets?.();
  }

  disconnectedCallback() {
    if (this.handlePopstate) {
      window.removeEventListener('popstate', this.handlePopstate);
    }
  }
/**
   * @param {boolean} variantAvailable
   * @param {string} variantId
   * @param {string} newProductId
   */
  async refreshAttentiveScripts(variantAvailable, variantId, newProductId) {
    
    if (window.attentive && typeof window.attentive.trigger === 'function') {
      window.attentive.trigger();
    }

    // Ensure the DOM has finished painting the new innerHTML from the fetch
    setTimeout(() => {
      const bisDiv = document.querySelector('#attn-waitlist-trigger');
      const bisButton = document.querySelector('#attn-waitlist-trigger-btn');
      const atc = document.querySelector('add-to-cart-component[ref="addToCartButtonContainer"]');
      
      // Check if we have the button container and its state
      const needsSizeSelection = (atc instanceof HTMLElement) && atc.dataset.needsSizeSelection === 'true';
      
      if (bisDiv && bisButton) {
        // Force update the attributes on the newly rendered elements
        bisDiv.setAttribute('data-product-id', newProductId || '');
        bisDiv.setAttribute('data-variant-id', variantId || '');

        // Logic for showing/hiding the BIS button based on availability
        if (!variantAvailable && !needsSizeSelection) {
          bisDiv.setAttribute('qty', 'false');
          bisDiv.style.display = 'block'; // Direct style set to ensure visibility
          if (atc) atc.classList.add('hidden');
        } else {
          if (atc) atc.classList.remove('hidden');
          bisDiv.removeAttribute('qty');
          bisDiv.style.display = 'none';
        }
      }
    }, 500);
  }

  setupNavigationHandling() {
    this.handlePopstate = (event) => {
      const currentUrl = window.location.href;
      const pathname = window.location.pathname;

      if (!pathname.includes('/products/')) {
        window.location.reload();
        return;
      }

      const url = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}section_id=${this.sectionId}`;
      const productContainer = this.closest('[data-product-information-content]');
      if (!productContainer) return;

      productContainer.classList.add('loading');

      const dataPromise = this.prefetchCache.has(url) 
        ? Promise.resolve(this.prefetchCache.get(url))
        : fetch(url).then(response => response.text());

      dataPromise
        .then(html => {
          const newDocument = new DOMParser().parseFromString(html, 'text/html');
          const baseSelectors = ['[data-product-top-header]', '[data-product-media-gallery]', '[data-product-details]'];
          
          const dynamicUpdateBlocks = productContainer.querySelectorAll('[data-product-update-block]');
          const dynamicSelectors = Array.from(dynamicUpdateBlocks).map(el => 
            `[data-product-update-block="${el.getAttribute('data-product-update-block')}"]`
          );

          [...baseSelectors, ...dynamicSelectors].forEach(selector => {
            const currentElement = productContainer.querySelector(selector);
            const newElement = newDocument.querySelector(selector);
            if (currentElement && newElement) currentElement.innerHTML = newElement.innerHTML;
          });

          this.updateYotpoReviewWidget(newDocument, productContainer);
        })
        .catch(e => console.error('Error updating product on navigation:', e))
        .finally(() => productContainer.classList.remove('loading'));
    };

    window.addEventListener('popstate', this.handlePopstate);
  }

  loadAllProductsVariants() {
    if (this.allProductsVariants) return;
    const script = this.querySelector('script[data-all-products-variants]');
    if (!script?.textContent) return;
    try {
      this.allProductsVariants = JSON.parse(script.textContent);
    } catch (e) {
      console.error('Failed to parse all-products-variants data:', e);
    }
  }

  findVariantFromPreloadedData(productHandle, size, width) {
    const result = { variantId: '', available: false, hasWidthOption: false };
    if (!this.allProductsVariants) return result;

    const productData = this.allProductsVariants[productHandle];
    if (!productData) return result;
    
    result.hasWidthOption = productData.hasWidthOption;

    if (!size) {
      // No size option (e.g. sunglasses, accessories, single-variant products):
      // fall back to the first available variant so availability reflects
      // reality instead of defaulting to false (which would force the
      // BIS / Notify Me button to show on in-stock items).
      const firstAvailable = productData.variants.find((v) => v.available);
      const fallback = firstAvailable || productData.variants[0];
      if (fallback) {
        result.variantId = String(fallback.id);
        result.available = fallback.available;
      }
      return result;
    }

    for (const variant of productData.variants) {
      const hasSize = variant.options.includes(size);
      if (productData.hasWidthOption && width) {
        if (hasSize && variant.options.includes(width)) {
          result.variantId = String(variant.id);
          result.available = variant.available;
          return result;
        }
      } else if (hasSize) {
        result.variantId = String(variant.id);
        result.available = variant.available;
        return result;
      }
    }
    return result;
  }

  captureSelectedOptions(productContainer) {
    const variantPicker = productContainer.querySelector('variant-picker');
    if (!variantPicker) return { size: null, width: null };

    const getVal = (selector) => {
      const el = variantPicker.querySelector(selector);
      return el ? el.querySelector('input:checked')?.value : null;
    };

    return { 
      size: getVal('[data-variant-option-size]'), 
      width: getVal('fieldset.variant-option--width') 
    };
  }

  getAnalyticsVariantId(newDocument, preferredVariantId) {
    if (preferredVariantId) return preferredVariantId;
    const script = newDocument.querySelector('variant-picker script[data-selected-variant]');
    try {
      return script ? String(JSON.parse(script.textContent).id) : '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Updates window.ShopifyAnalytics.meta.selectedVariantId to reflect the
   * shopper's currently selected variant. Shopify only sets this on initial
   * page load, so it must be kept in sync on client-side variant changes for
   * Back in Stock eligibility to resolve to the correct variant.
   * @param {string} variantId - The currently selected variant ID (string).
   */
  syncShopifyAnalyticsSelectedVariant(variantId) {
    if (!variantId) return;
    const analytics = window.ShopifyAnalytics;
    if (!analytics) return;
    analytics.meta = analytics.meta || {};
    analytics.meta.selectedVariantId = String(variantId);
  }

  updateUrlWithVariant(newVariantId) {
    setTimeout(() => {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', newVariantId);
      window.history.replaceState({}, '', url.toString());
    }, 0);
  }

  updateYotpoReviewWidget(newDocument, productContainer) {
    const newCS = newDocument.querySelector('color-selector');
    const widget = document.querySelector('[data-v-app]');
    if (!newCS || !widget) return;

    const mapping = {
      yotpoProductId: 'productId', yotpoName: 'productName', yotpoUrl: 'productUrl',
      yotpoPrice: 'productPrice', yotpoCurrency: 'productCurrency',
      yotpoImageUrl: 'productImage', yotpoDescription: 'productDescription'
    };

    Object.entries(mapping).forEach(([attr, dataKey]) => {
      if (newCS.dataset[dataKey]) widget.dataset[attr] = newCS.dataset[dataKey];
    });
    window.yotpoWidgetsContainer?.initWidgets?.();
  }

  prefetchVariant(url) {
    if (this.prefetchCache.has(url) || this.prefetchInProgress.has(url)) return;
    this.prefetchInProgress.add(url);
    fetch(url)
      .then(r => r.text())
      .then(html => {
        this.prefetchCache.set(url, html);
        this.prefetchInProgress.delete(url);
      })
      .catch(() => this.prefetchInProgress.delete(url));
  }

  setupPrefetch() {
    const prefetchAll = () => {
      const container = this.closest('[data-product-information-content]');
      const saved = container ? this.captureSelectedOptions(container) : { size: null, width: null };
      
      this.querySelectorAll('.color-selector__swatch:not(.is-active)').forEach(swatch => {
        const handle = swatch.dataset.productHandle;
        if (!handle || !this.allProductsVariants?.[handle]) return;

        let url = `${this.allProductsVariants[handle].url}?section_id=${this.sectionId}`;
        if (saved.size) {
          const res = this.findVariantFromPreloadedData(handle, saved.size, saved.width);
          if (res.variantId) url += `&variant=${res.variantId}`;
        }
        this.prefetchVariant(url);
      });
    };

    'requestIdleCallback' in window ? requestIdleCallback(prefetchAll) : setTimeout(prefetchAll, 1500);
  }

  setupEventListeners() {
    this.addEventListener('mouseover', (e) => {
      const swatch = e.target.closest('.color-selector__swatch');
      if (!swatch || swatch.classList.contains('is-active')) return;
      const handle = swatch.dataset.productHandle;
      if (!handle || !this.allProductsVariants?.[handle]) return;

      const container = this.closest('[data-product-information-content]');
      const saved = container ? this.captureSelectedOptions(container) : { size: null, width: null };
      let url = `${this.allProductsVariants[handle].url}?section_id=${this.sectionId}`;
      const res = this.findVariantFromPreloadedData(handle, saved.size, saved.width);
      if (res.variantId) url += `&variant=${res.variantId}`;
      this.prefetchVariant(url);
    });

    this.addEventListener('click', async (e) => {
      if (!this.sectionRenderingColorSwapping) return;
      const swatch = e.target.closest('.color-selector__swatch');
      if (!swatch) return;
      e.preventDefault();

      const handle = swatch.dataset.productHandle;
      const container = this.closest('[data-product-information-content]');
      if (!handle || !this.allProductsVariants?.[handle] || !container) return;

      const previousProductId = this.dataset.productId;
      const saved = this.captureSelectedOptions(container);
      const res = this.findVariantFromPreloadedData(handle, saved.size, saved.width);
      const productUrl = this.allProductsVariants[handle].url;
      let url = `${productUrl}?section_id=${this.sectionId}`;
      if (res.variantId) url += `&variant=${res.variantId}`;

      const swapId = ++this.activeSwapId;
      const ctx = { container, productUrl, previousProductId, saved, res };
      const cachedHtml = this.prefetchCache.get(url);

      if (cachedHtml) {
        // Fast path: apply prefetched HTML immediately so the UI swaps
        // without waiting on the network.
        this.applyColorSwapHtml(cachedHtml, ctx);
        // Then quietly refresh the cache so a follow-up click on this
        // color picks up any server-side changes (inventory, price, etc.).
        this.refreshPrefetchCache(url, swapId);
        return;
      }

      // Cold path: no prefetched HTML available yet. Fall back to a
      // network fetch but populate the cache so future clicks are fast.
      container.classList.add('loading');
      try {
        const response = await fetch(url);
        const html = await response.text();
        if (this.activeSwapId !== swapId) return;
        this.prefetchCache.set(url, html);
        this.applyColorSwapHtml(html, ctx);
      } catch (err) {
        console.error(err);
      } finally {
        container.classList.remove('loading');
      }
    });
  }

  /**
   * Applies a fetched section HTML response to the page, replacing the
   * relevant product containers, updating analytics/Yotpo/Attentive, and
   * dispatching the `color-change` event so variant-picker and
   * product-form re-sync.
   * @param {string} html - Full section render HTML.
   * @param {{
   *   container: Element,
   *   productUrl: string,
   *   previousProductId: string|undefined,
   *   saved: { size: string|null, width: string|null },
   *   res: { variantId: string, available: boolean, hasWidthOption: boolean }
   * }} ctx - Context captured at click time.
   */
  applyColorSwapHtml(html, ctx) {
    const { container, productUrl, previousProductId, saved, res } = ctx;
    const newDoc = new DOMParser().parseFromString(html, 'text/html');

    ['[data-product-top-header]', '[data-product-media-gallery]', '[data-product-details]'].forEach(sel => {
      const curEl = container.querySelector(sel);
      const newEl = newDoc.querySelector(sel);
      if (curEl && newEl) curEl.innerHTML = newEl.innerHTML;
    });

    this.updateYotpoReviewWidget(newDoc, container);

    // Gather new product ID from the fetched response
    const newCS = newDoc.querySelector('color-selector');
    const newProductId = newCS?.dataset?.productId;

    const varScript = newDoc.querySelector('variant-picker script[data-variants]');
    const analyticId = this.getAnalyticsVariantId(newDoc, res.variantId);

    // Keep the Shopify page object in sync with the newly selected color's variant.
    // Attentive reads window.ShopifyAnalytics.meta.selectedVariantId to determine
    // Back in Stock eligibility, and Shopify does not update it on client-side swaps.
    // Stored as a string, which is what the analytics meta expects.
    if (analyticId) this.syncShopifyAnalyticsSelectedVariant(analyticId);

    if (varScript?.textContent && analyticId) {
      try {
        const data = JSON.parse(varScript.textContent);
        const selVar = data.variants?.find(v => String(v.id) === analyticId);
        if (data.product && selVar) window.ThemeAnalytics?.pushViewItem?.(data.product, selVar);
      } catch (err) { console.error(err); }
    }

    window.history.replaceState({}, '', productUrl);
    if (res.variantId) this.updateUrlWithVariant(res.variantId);

    this.dispatchEvent(new CustomEvent('color-change', {
      bubbles: true,
      detail: { previousProductId, newProductId, savedOptions: saved, newVariantId: res.variantId, variantAvailable: res.available, productUrl }
    }));

    // Pass the newProductId gathered from the response
    this.refreshAttentiveScripts(res.available, res.variantId, newProductId);
  }

  /**
   * Background fetch used after a fast-path swap to keep the prefetch
   * cache aligned with the latest server-rendered HTML. Does not touch
   * the DOM so it cannot disrupt the user's in-progress interactions.
   * @param {string} url - The section render URL whose cache entry to refresh.
   * @param {number} swapId - The swap counter value at fetch start.
   */
  async refreshPrefetchCache(url, swapId) {
    try {
      const response = await fetch(url);
      const html = await response.text();
      // If the user has clicked another swatch in the meantime, the cache
      // entry for that other URL is what matters; this one is no longer
      // the active selection so just refresh it without further action.
      this.prefetchCache.set(url, html);
      if (this.activeSwapId !== swapId) return;
    } catch (err) {
      console.error('Background prefetch refresh failed:', err);
    }
  }
}

customElements.define('color-selector', ColorSelector);